import { useState, useEffect, useCallback } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Coins, Zap, TrendingUp, Clock, ShieldCheck, ArrowUpRight,
  Loader2, Package, CreditCard, AlertTriangle, CheckCircle2,
  Image, BookOpen, Brain, BarChart2, FileText, Globe, Sparkles, RotateCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { CreditUsageCharts } from "../components/subscription/CreditUsageCharts";
import { AutoRefillCard } from "../components/subscription/AutoRefillCard";

const ACTION_LABELS = {
  image_gen_dish: { label: "Imagen de plato", icon: Image },
  image_gen_ingredient: { label: "Imagen ingrediente", icon: Image },
  image_gen_logo: { label: "Logo", icon: Sparkles },
  recipe_gen: { label: "Receta", icon: BookOpen },
  menu_gen: { label: "Menu completo", icon: FileText },
  forecast: { label: "Prevision IA", icon: BarChart2 },
  invoice_extract: { label: "Extraer factura", icon: FileText },
  menu_import: { label: "Importar menu", icon: FileText },
  brand_ambiance: { label: "Identidad visual", icon: Sparkles },
  website_content: { label: "Contenido web", icon: Globe },
  waste_estimate: { label: "Estimar merma", icon: Brain },
  allergen_ai: { label: "Alergenos IA", icon: Brain },
  admin_top_up: { label: "Recarga admin", icon: ArrowUpRight },
  // ── Refunds: distinguen visualmente devoluciones de gastos ────────
  auto_refund_recipe_failed: { label: "Reembolso · receta IA fallida", icon: RotateCcw },
  auto_refund_image_failed: { label: "Reembolso · imagen IA fallida", icon: RotateCcw },
  auto_refund_allergens_failed: { label: "Reembolso · alérgenos IA fallidos", icon: RotateCcw },
  refund_batch_failed: { label: "Reembolso por lote fallido", icon: RotateCcw },
  bug_compensation: { label: "Compensación por bug (cortesía)", icon: RotateCcw },
};

const STATUS_MAP = {
  trial: { label: "Prueba", color: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  active: { label: "Activo", color: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  expired: { label: "Expirado", color: "bg-red-500/10 text-red-400 border-red-500/20" },
  trial_expired: { label: "Prueba expirada", color: "bg-red-500/10 text-red-400 border-red-500/20" },
};

export default function SubscriptionPage() {
  const { currentRestaurant } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [subData, setSubData] = useState(null);
  const [creditData, setCreditData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [topUpPacks, setTopUpPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [pendingUpgrade, setPendingUpgrade] = useState(null);
  const [requestingUpgrade, setRequestingUpgrade] = useState(null);
  // Quick filter for "Últimas operaciones" — owner con 200+ txns lo agradece
  const [txnFilter, setTxnFilter] = useState("all"); // all | charges | refunds | topups
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [autoRefill, setAutoRefill] = useState(null);

  const rid = currentRestaurant?.restaurant_id;

  const loadData = useCallback(async () => {
    if (!rid) return;
    // Use allSettled so that one failing endpoint doesn't leave the whole
    // page blank. Notably, this allows the "Sin suscripción activa" empty
    // state to still show the catalog of plans even if /credits or
    // /top-up-packs hiccup (timeout, 502, etc.).
    const [sRes, cRes, pRes, tRes] = await Promise.allSettled([
      api.get(`/subscription/my/${rid}`),
      api.get(`/subscription/credits/${rid}`),
      api.get("/subscription/plans"),
      api.get("/subscription/top-up-packs"),
    ]);

    if (sRes.status === "fulfilled") setSubData(sRes.value);
    else console.error("[SubscriptionPage] /subscription/my failed:", sRes.reason);

    if (cRes.status === "fulfilled") setCreditData(cRes.value);
    else console.error("[SubscriptionPage] /subscription/credits failed:", cRes.reason);

    if (pRes.status === "fulfilled") setPlans(pRes.value || []);
    else console.error("[SubscriptionPage] /subscription/plans failed:", pRes.reason);

    if (tRes.status === "fulfilled") setTopUpPacks(tRes.value || []);
    else console.error("[SubscriptionPage] /subscription/top-up-packs failed:", tRes.reason);

    // Auto-refill config (independent, never blocks main render)
    api.getAutoRefill(rid)
      .then(setAutoRefill)
      .catch(() => setAutoRefill(null));

    // Check pending upgrade (independent, never blocks main render)
    api.get(`/subscription/upgrade-status/${rid}`)
      .then(r => setPendingUpgrade(r.pending_request))
      .catch(() => setPendingUpgrade(null));

    setLoading(false);
  }, [rid]);

  useEffect(() => { loadData(); }, [loadData]);

  // Handle Stripe redirect for auto-refill card setup (mode=setup checkout).
  // Runs independently of the top-up/subscription poller below.
  useEffect(() => {
    const autorefill = searchParams.get("autorefill");
    if (!autorefill || !rid) return;

    if (autorefill === "cancelled") {
      toast.info("Guardado de tarjeta cancelado");
      setSearchParams({});
      return;
    }
    if (autorefill === "card_saved") {
      const setupSessionId = searchParams.get("setup_session_id");
      if (!setupSessionId) { setSearchParams({}); return; }
      api.completeAutoRefillSetup(rid, setupSessionId)
        .then((res) => {
          if (res?.saved) {
            toast.success("Tarjeta guardada. Ya puedes activar la recarga automática.");
          } else {
            toast.error("No se pudo confirmar el guardado de la tarjeta");
          }
          // El backend devuelve el config actualizado junto a { saved }.
          setAutoRefill((prev) => ({ ...(prev || {}), ...res }));
        })
        .catch((e) => toast.error(e?.message || "No se pudo confirmar la tarjeta"))
        .finally(() => setSearchParams({}));
    }
  }, [searchParams, rid]);

  // Handle Stripe redirect — poll payment status
  // This runs independently of restaurant loading, since returning from Stripe
  // may temporarily lose cookie-based auth
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const payment = searchParams.get("payment");
    if (!sessionId) return;

    if (payment === "cancelled") {
      toast.info("Pago cancelado");
      setSearchParams({});
      return;
    }

    const pollPayment = async (attempts = 0) => {
      if (attempts >= 12) {
        toast.error("No se pudo verificar el pago. Revisa tu email para confirmacion.");
        setCheckingPayment(false);
        setSearchParams({});
        return;
      }
      setCheckingPayment(true);
      try {
        const result = await api.getCheckoutStatus(sessionId);
        if (result.payment_status === "paid" || result.payment_status === "completed") {
          if (result.type === "top_up") {
            toast.success("Creditos anadidos correctamente");
          } else {
            toast.success("Suscripcion activada correctamente");
          }
          setCheckingPayment(false);
          setSearchParams({});
          loadData();
          return;
        }
        if (result.status === "expired") {
          toast.error("La sesion de pago ha expirado");
          setCheckingPayment(false);
          setSearchParams({});
          return;
        }
        setTimeout(() => pollPayment(attempts + 1), 2500);
      } catch {
        // Retry — may fail initially if cookies not yet available
        setTimeout(() => pollPayment(attempts + 1), 2500);
      }
    };

    if (payment === "success" || payment === "topup_success") {
      pollPayment();
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-open sections based on ?tab= param (driven by throttle toasts).
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (!tab) return;
    if (tab === "plans") {
      setShowUpgrade(true);
    } else if (tab === "credits" || tab === "top-up") {
      // Defer scroll until the top-up card renders.
      setTimeout(() => {
        document.getElementById("topup-packs")?.scrollIntoView({
          behavior: "smooth", block: "start",
        });
      }, 300);
    } else if (tab === "autorefill") {
      setTimeout(() => {
        document.getElementById("autorefill-section")?.scrollIntoView({
          behavior: "smooth", block: "center",
        });
      }, 400);
    }
  }, [searchParams]);

  const suggestedPackId = searchParams.get("suggested");

  // Stripe checkout for subscription (new or upgrade with proration)
  const handleSubscribe = async (planId) => {
    setRequestingUpgrade(planId);
    try {
      const result = await api.createSubscriptionCheckout(planId, rid, billingPeriod);
      if (result.is_upgrade && result.proration_credit > 0) {
        toast.info(
          `Upgrade con prorrateo: ${result.original_price.toFixed(2)} EUR - ${result.proration_credit.toFixed(2)} EUR credito = ${result.amount.toFixed(2)} EUR`,
          { duration: 5000 }
        );
      }
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (e) {
      toast.error(e?.message || "Error al iniciar el pago");
    }
    setRequestingUpgrade(null);
  };

  // Stripe checkout for top-up
  const handleTopUp = async (packId) => {
    setRequesting(true);
    try {
      const result = await api.createTopUpCheckout(packId, rid);
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (e) {
      toast.error(e?.message || "Error al iniciar la compra");
    }
    setRequesting(false);
  };

  const handleRequestUpgrade = async (planId) => {
    // Direct Stripe checkout for upgrades
    await handleSubscribe(planId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  const sub = subData?.subscription;
  const plan = subData?.plan;
  const rules = creditData?.rules || {};
  const transactions = creditData?.transactions || [];

  const pctUsed = sub?.pct_used || 0;
  const barColor = pctUsed >= 90 ? "bg-red-500" : pctUsed >= 70 ? "bg-amber-500" : "bg-emerald-500";
  const statusInfo = STATUS_MAP[sub?.status] || STATUS_MAP.expired;

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="subscription-page">
      {/* Payment processing overlay */}
      {checkingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-card border rounded-2xl p-8 text-center shadow-2xl max-w-sm mx-4">
            <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">Verificando pago...</h3>
            <p className="text-sm text-muted-foreground">Esto puede tardar unos segundos</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
          <Coins className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-xl font-bold">Suscripcion y Creditos</h1>
          <p className="text-muted-foreground text-xs">Gestiona tu plan y consumo de IA</p>
        </div>
      </div>

      {!sub ? (
        /* No subscription */
        <div className="bg-card border rounded-xl p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Sin suscripcion activa</h2>
          <p className="text-muted-foreground text-sm mb-6">
            Elige el plan que mejor se adapte a tu restaurante y empieza a usar las funciones de IA.
          </p>
          {plans.filter(p => !p.is_trial).length === 0 ? (
            <div className="max-w-md mx-auto p-4 border border-dashed border-amber-500/30 rounded-lg bg-amber-500/5" data-testid="plans-load-error">
              <p className="text-sm text-muted-foreground mb-3">
                No hemos podido cargar el catálogo de planes. Por favor refresca la página o contacta con soporte si el problema persiste.
              </p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => loadData()}
                data-testid="reload-plans-btn"
              >
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reintentar
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
              {plans.filter(p => !p.is_trial).map((p) => (
              <div key={p.plan_id} className="border rounded-xl p-4 text-left" data-testid={`plan-card-${p.plan_id}`}>
                <h3 className="font-semibold text-sm">{p.name}</h3>
                <p className="text-2xl font-bold mt-1">{p.price}<span className="text-sm text-muted-foreground font-normal"> EUR/mes + IVA</span></p>
                <p className="text-xs text-muted-foreground mt-1">{p.credits_monthly} creditos/mes</p>
                <ul className="mt-3 space-y-1">
                  {(p.features || []).slice(0, 3).map((f, i) => (
                    <li key={`feat-${i}-${f}`} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  size="sm"
                  className="w-full mt-3 rounded-full gap-1.5"
                  disabled={requestingUpgrade === p.plan_id}
                  onClick={() => handleSubscribe(p.plan_id)}
                  data-testid={`subscribe-btn-${p.plan_id}`}
                >
                  {requestingUpgrade === p.plan_id
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Procesando...</>
                    : <><CreditCard className="w-3.5 h-3.5" /> Suscribirse</>
                  }
                </Button>
              </div>
            ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Active subscription */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Plan info */}
            <div className="bg-card border rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Plan actual</span>
                <Badge className={statusInfo.color} data-testid="subscription-status">{statusInfo.label}</Badge>
              </div>
              <h2 className="text-2xl font-bold" data-testid="subscription-plan-name">{sub.plan_name}</h2>
              <p className="text-muted-foreground text-xs mt-1">
                {sub.days_remaining > 0 ? `${sub.days_remaining} dias restantes` : "Periodo finalizado"}
              </p>
              {sub.max_restaurants && (
                <p className="text-muted-foreground text-[10px] mt-0.5">
                  Hasta {sub.max_restaurants} restaurante{sub.max_restaurants > 1 ? "s" : ""}
                </p>
              )}
              <Button
                size="sm"
                className="mt-3 w-full gap-1.5 rounded-full"
                onClick={() => setShowUpgrade(true)}
                data-testid="upgrade-plan-btn"
              >
                <ArrowUpRight className="w-3.5 h-3.5" /> Upgrade de plan
              </Button>
            </div>

            {/* Credits gauge */}
            <div className="bg-card border rounded-xl p-5 col-span-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground uppercase tracking-wider">Creditos IA</span>
                <span className="text-xs font-medium" data-testid="credits-counter">
                  {sub.credits_remaining} / {sub.credits_total}
                </span>
              </div>
              <div className="h-3 bg-muted rounded-full overflow-hidden mb-2">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                  style={{ width: `${Math.min(100, pctUsed)}%` }}
                  data-testid="credits-bar"
                />
              </div>
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>{sub.credits_used} usados</span>
                <span>{pctUsed}% consumido</span>
              </div>

              {/* Alert thresholds */}
              {pctUsed >= 90 && (
                <div className="mt-3 p-2 bg-red-500/10 rounded-lg flex items-center gap-2 text-red-400 text-xs" data-testid="credit-alert-critical">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  {pctUsed >= 100 ? "Creditos agotados — las funciones de IA estan bloqueadas" : "Quedan menos del 10% de creditos"}
                </div>
              )}
              {pctUsed >= 70 && pctUsed < 90 && (
                <div className="mt-3 p-2 bg-amber-500/10 rounded-lg flex items-center gap-2 text-amber-400 text-xs" data-testid="credit-alert-warning">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  Mas del 70% de creditos consumidos — considera recargar
                </div>
              )}

              {/* Onboarding bonus split — only shown for plans that grant a bonus */}
              {(sub.onboarding_credits_total ?? 0) > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-3" data-testid="onboarding-bonus-breakdown">
                  <div className="rounded-lg border border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-amber-500/5 p-3">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-orange-500 font-semibold mb-1">
                      <Sparkles className="w-3 h-3" /> Bonus de bienvenida
                    </div>
                    <p className="text-xl font-bold tracking-tight">
                      {Math.max(0, (sub.onboarding_credits_total || 0) - (sub.onboarding_credits_used || 0))}
                      <span className="text-xs text-muted-foreground font-normal"> / {sub.onboarding_credits_total}</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Se gasta primero · no caduca</p>
                  </div>
                  <div className="rounded-lg border border-border bg-muted/30 p-3">
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">
                      Mensuales
                    </div>
                    <p className="text-xl font-bold tracking-tight">
                      {Math.max(0, (sub.credits_monthly_total || 0) - (sub.credits_monthly_used || 0))}
                      <span className="text-xs text-muted-foreground font-normal"> / {sub.credits_monthly_total}</span>
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Se renuevan cada mes</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Top-up packs */}
          {sub.top_up_enabled && topUpPacks.length > 0 && (
            <div id="topup-packs" className="bg-card border rounded-xl p-5 scroll-mt-24">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Package className="w-4 h-4 text-muted-foreground" />
                Recargar creditos
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {topUpPacks.map((pack) => {
                  const isSuggested = pack.pack_id === suggestedPackId;
                  return (
                  <div
                    key={pack.pack_id}
                    className={`border rounded-lg p-4 flex flex-col relative ${isSuggested ? "ring-2 ring-primary border-primary bg-primary/5" : ""}`}
                    data-testid={`topup-${pack.pack_id}`}
                  >
                    {isSuggested && (
                      <span className="absolute -top-2 left-3 px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wide">
                        Recomendado
                      </span>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-lg font-bold">{pack.credits}</span>
                      <span className="text-xs text-muted-foreground">creditos</span>
                    </div>
                    <span className="text-sm font-medium mb-3">{pack.price} EUR</span>
                    <Button
                      size="sm"
                      variant={isSuggested ? "default" : "outline"}
                      className="mt-auto"
                      disabled={requesting}
                      onClick={() => handleTopUp(pack.pack_id)}
                      data-testid={`topup-btn-${pack.pack_id}`}
                    >
                      <CreditCard className="w-3.5 h-3.5 mr-1.5" />
                      {requesting ? "Procesando..." : "Comprar"}
                    </Button>
                  </div>
                  );
                })}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Pago seguro con Stripe. Los creditos se anaden inmediatamente.
              </p>
            </div>
          )}

          {/* Auto-refill de créditos IA (cobro off-session) */}
          <AutoRefillCard
            restaurantId={rid}
            config={autoRefill}
            packs={topUpPacks}
            onChanged={setAutoRefill}
          />

          {/* Pending upgrade request banner */}
          {pendingUpgrade && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center gap-3" data-testid="pending-upgrade-banner">
              <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-sm font-semibold">Solicitud de upgrade pendiente</p>
                <p className="text-xs text-muted-foreground">Tu solicitud a <strong>{pendingUpgrade.target_plan_name}</strong> esta siendo revisada por el administrador</p>
              </div>
            </div>
          )}

          {/* Upgrade Plans Modal */}
          {showUpgrade && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="upgrade-modal">
              <div className="bg-card border rounded-2xl w-full max-w-3xl mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold flex items-center gap-2">
                      <ArrowUpRight className="w-5 h-5 text-primary" />
                      Mejorar mi plan
                    </h2>
                    <p className="text-xs text-muted-foreground mt-1">Plan actual: <strong>{sub.plan_name}</strong></p>
                  </div>
                  <button onClick={() => setShowUpgrade(false)} className="text-muted-foreground hover:text-foreground transition-colors text-xl leading-none">&times;</button>
                </div>
                <div className="p-6">
                  {pendingUpgrade ? (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 text-primary/40 mx-auto mb-3" />
                      <p className="font-semibold">Ya tienes una solicitud pendiente</p>
                      <p className="text-sm text-muted-foreground mt-1">Plan solicitado: <strong>{pendingUpgrade.target_plan_name}</strong></p>
                    </div>
                  ) : (
                    <>
                      {/* Billing period toggle */}
                      <div className="flex items-center justify-center gap-2 mb-6" data-testid="billing-toggle">
                        <button
                          onClick={() => setBillingPeriod("monthly")}
                          className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${billingPeriod === "monthly" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                        >
                          Mensual
                        </button>
                        <button
                          onClick={() => setBillingPeriod("annual")}
                          className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${billingPeriod === "annual" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                        >
                          Anual
                          {plans.some(p => p.annual_savings > 0) && (
                            <span className="ml-1.5 px-1.5 py-0.5 bg-emerald-500/20 text-emerald-400 text-[9px] rounded-full font-bold">AHORRA</span>
                          )}
                        </button>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {plans.filter(p => !p.is_trial && p.plan_id !== sub.plan_id && p.price > (plan?.price || 0)).length === 0 ? (
                          <div className="col-span-full text-center py-10 px-4" data-testid="no-upgrade-available">
                            <CheckCircle2 className="w-12 h-12 text-emerald-500/40 mx-auto mb-3" />
                            <p className="font-semibold text-base">Ya tienes nuestro plan más alto</p>
                            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                              Estás en <strong>{sub.plan_name}</strong>, la suscripción superior disponible. Si necesitas más créditos IA, puedes adquirir packs extra desde la sección "Recargar créditos".
                            </p>
                            <Button
                              variant="outline"
                              className="mt-4 rounded-full gap-1.5"
                              onClick={() => {
                                setShowUpgrade(false);
                                document.querySelector('[data-testid="top-up-section"]')?.scrollIntoView({ behavior: "smooth" });
                              }}
                              data-testid="go-to-topup-btn"
                            >
                              <Coins className="w-3.5 h-3.5" /> Ver packs de créditos
                            </Button>
                          </div>
                        ) : plans.filter(p => !p.is_trial && p.plan_id !== sub.plan_id && p.price > (plan?.price || 0)).map(p => {
                          const hasOffer = !!p.offer;
                          const isAnnual = billingPeriod === "annual";

                          let displayPrice, displayPeriod, originalPrice, savingsText;
                          if (isAnnual) {
                            const finalAnnual = hasOffer && p.offer_price_annual ? p.offer_price_annual : p.price_annual_final;
                            displayPrice = finalAnnual;
                            displayPeriod = "/ano + IVA";
                            originalPrice = (hasOffer && p.offer_price_annual) ? p.price_annual_final : (p.annual_savings > 0 ? p.price_annual || p.price * 12 : null);
                            const totalSavings = (p.price_annual || p.price * 12) - finalAnnual;
                            savingsText = totalSavings > 0 ? `Ahorras ${totalSavings.toFixed(0)}EUR` : null;
                          } else {
                            const finalMonthly = hasOffer && p.offer_price_monthly ? p.offer_price_monthly : p.price;
                            displayPrice = finalMonthly;
                            displayPeriod = "/mes + IVA";
                            originalPrice = hasOffer ? p.price : null;
                            savingsText = null;
                          }

                          const creditsLabel = isAnnual ? `${p.credits_monthly * 12} creditos/ano` : `${p.credits_monthly} creditos/mes`;

                          return (
                            <div key={p.plan_id} className="relative border rounded-xl p-5 flex flex-col transition-all hover:border-primary/40 hover:shadow-md" data-testid={`upgrade-plan-${p.plan_id}`}>
                              {p.plan_id === "plan_pro" && (
                                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full uppercase tracking-wide">Popular</div>
                              )}
                              {hasOffer && (
                                <div className="absolute -top-2.5 right-3 px-2 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full uppercase">{p.offer.name}</div>
                              )}
                              <h3 className="font-bold text-base">{p.name}</h3>
                              <p className="text-[10px] text-muted-foreground">Hasta {p.max_restaurants || 1} restaurante{(p.max_restaurants || 1) > 1 ? "s" : ""}</p>
                              <div className="mt-2 flex items-baseline gap-2">
                                <span className="text-3xl font-extrabold">{displayPrice.toFixed ? displayPrice.toFixed(0) : displayPrice}&euro;</span>
                                <span className="text-sm text-muted-foreground">{displayPeriod}</span>
                              </div>
                              {originalPrice && (
                                <span className="text-xs text-muted-foreground line-through">{originalPrice.toFixed ? originalPrice.toFixed(0) : originalPrice}&euro;</span>
                              )}
                              {savingsText && <span className="text-[10px] text-emerald-500 font-semibold">{savingsText}</span>}
                              {p.annual_discount_label && isAnnual && !hasOffer && <span className="text-[10px] text-primary font-medium">{p.annual_discount_label}</span>}
                              <p className="text-xs text-muted-foreground mt-1 mb-4">{creditsLabel}</p>
                              <ul className="space-y-1.5 flex-1 mb-4">
                                {(p.features || []).map((f, i) => (
                                  <li key={`pf-${i}-${f}`} className="flex items-start gap-1.5 text-xs">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <span>{f}</span>
                                  </li>
                                ))}
                              </ul>
                              <Button
                                className="w-full rounded-full gap-1.5"
                                disabled={requestingUpgrade === p.plan_id}
                                onClick={() => handleRequestUpgrade(p.plan_id)}
                                data-testid={`request-upgrade-${p.plan_id}`}
                              >
                                {requestingUpgrade === p.plan_id
                                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Procesando...</>
                                  : <><CreditCard className="w-3.5 h-3.5" /> {isAnnual ? "Suscribirse anual" : "Suscribirse"}</>
                                }
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                  <p className="text-[10px] text-muted-foreground text-center mt-4">
                    Pago seguro con Stripe. Solo se cobra la diferencia prorrateada por los dias restantes de tu plan actual.
                  </p>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Credit Rules */}
      <div className="bg-card border rounded-xl p-5">
        <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Zap className="w-4 h-4 text-muted-foreground" />
          Coste por funcion IA
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {Object.entries(rules).map(([action, credits]) => {
            const info = ACTION_LABELS[action] || { label: action, icon: Brain };
            const Icon = info.icon;
            return (
              <div key={action} className="flex items-center gap-2 p-2 bg-muted/50 rounded-lg" data-testid={`rule-${action}`}>
                <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-xs truncate block">{info.label}</span>
                </div>
                <span className="text-xs font-bold text-primary">{credits}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Credit Usage Charts + History ────────────────────────────── */}
      <div className="bg-card border rounded-xl p-5">
        <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-muted-foreground" />
          Historial de consumo
        </h3>
        <CreditUsageCharts
          restaurantId={rid}
          subscription={sub}
          topUpPacks={topUpPacks}
        />
      </div>

      {/* Recent Transactions list */}
      {transactions.length > 0 && (() => {
        const isRefundTxn = (t) => {
          const a = t.action || "";
          return a.startsWith("refund_") || a.startsWith("auto_refund_") || a === "bug_compensation";
        };
        const isTopUpTxn = (t) => !isRefundTxn(t) && (t.credits_used || 0) < 0;
        const isChargeTxn = (t) => (t.credits_used || 0) > 0;
        const filtered = transactions.filter((t) => {
          if (txnFilter === "charges") return isChargeTxn(t);
          if (txnFilter === "refunds") return isRefundTxn(t);
          if (txnFilter === "topups")  return isTopUpTxn(t);
          return true;
        });
        const chips = [
          { key: "all",     label: "Todas",      count: transactions.length },
          { key: "charges", label: "Cargos",     count: transactions.filter(isChargeTxn).length },
          { key: "refunds", label: "Reembolsos", count: transactions.filter(isRefundTxn).length },
          { key: "topups",  label: "Top-ups",    count: transactions.filter(isTopUpTxn).length },
        ];
        return (
        <div className="bg-card border rounded-xl p-5">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              Últimas operaciones
            </h3>
            <div className="flex gap-1.5" data-testid="txn-filter-chips">
              {chips.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setTxnFilter(c.key)}
                  data-testid={`txn-chip-${c.key}`}
                  disabled={c.count === 0 && c.key !== "all"}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                    txnFilter === c.key
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-transparent text-muted-foreground border-border hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                  }`}
                >
                  {c.label}{c.count > 0 ? <span className="ml-1 opacity-70">({c.count})</span> : ""}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="py-6 text-center text-xs text-muted-foreground italic">
                No hay operaciones de tipo "{chips.find((c) => c.key === txnFilter)?.label.toLowerCase()}".
              </div>
            ) : filtered.slice(0, 30).map((txn, i) => {
              const isRefund = (txn.action || "").startsWith("refund_") || (txn.action || "").startsWith("auto_refund_") || txn.action === "bug_compensation";
              const fallbackInfo = isRefund
                ? { label: "Reembolso", icon: RotateCcw }
                : { label: txn.action, icon: Brain };
              const info = ACTION_LABELS[txn.action] || fallbackInfo;
              const isTopUp = txn.credits_used < 0; // top-up + refund both negative
              return (
                <div key={txn.transaction_id || `txn-${i}`} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isTopUp ? "bg-emerald-500/10" : "bg-muted"
                  }`}>
                    <info.icon className={`w-3.5 h-3.5 ${isTopUp ? "text-emerald-400" : "text-muted-foreground"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs truncate block">{info.label}</span>
                    {txn.details && <span className="text-[10px] text-muted-foreground truncate block">{txn.details}</span>}
                  </div>
                  <div className="text-right">
                    <span className={`text-xs font-medium ${isTopUp ? "text-emerald-400" : ""}`}>
                      {isTopUp ? "+" : "-"}{Math.abs(txn.credits_used)}
                    </span>
                    <span className="text-[10px] text-muted-foreground block">
                      {new Date(txn.timestamp).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        );
      })()}
    </div>
  );
}
